<template>
  <div id="home" class="bg-cl-main-smoke">
    <!-- Add WebSite rich snippet -->
    <div class="hidden" itemscope itemtype="http://schema.org/WebSite">
      <link itemprop="url" href="https://kultowy.pl/">
      <meta itemprop="name" content="Internetowy sklep - Kultowy.pl">
      <meta
        itemprop="alternateName"
        content="Internetowy sklep - Kultowy.pl - najpopularniejsze książki w najniższych cenach"
      >
      <form itemprop="potentialAction" itemscope itemtype="http://schema.org/SearchAction">
        <meta
          itemprop="target"
          itemtype="http://schema.org/EntryPoint"
          content="https://kultowy.pl/sklep?query={search_term_string}"
        >
        <input itemprop="query-input" required name="search_term_string">
        <input type="submit">
      </form>
    </div>
    <div class="py10 center-xs mb10 bg-cl-white shadow-4 logo-box visible-md">
      <logo width="90px" height="20px" />
    </div>
    <logo-categories class="visible-md" />
    <main-banners-block-cms :valid-banners="validBaners['mainpl']" />
    <logos-block-cms :valid-banners="validBaners['logos']" />
    <lazy-hydrate :trigger-hydration="!loading" v-if="isLazyHydrateEnabled">
      <product-tabs
        :products="productsByTypes"
      />
    </lazy-hydrate>
    <product-tabs
      v-else
      :products="productsByTypes"
    />
    <LazyHydrate when-visible>
      <banners-block-cms :valid-banners="validBaners['second']" />
    </LazyHydrate>
    <section class="new-collection container px15 mb40">
      <div>
        <header class="col-md-12">
          <h2 class="align-left h4 secondary-font pl20 py40 m0">
            {{ $t('Recommend') }}
          </h2>
        </header>
      </div>
      <div class="row center-xs">
        <lazy-hydrate :trigger-hydration="!loading" v-if="isLazyHydrateEnabled">
          <products-carousel list="polecamy" :products="getPromoBlockCollection" />
        </lazy-hydrate>
        <products-carousel v-else list="polecamy" :products="getPromoBlockCollection" />
      </div>
    </section>

    <circle-categories />
  </div>
</template>

<script>
// query constructor
import { isServer } from '@vue-storefront/core/helpers'
import LazyHydrate from 'vue-lazy-hydration'
// Theme local components
import { Logger } from '@vue-storefront/core/lib/logger'
import { mapGetters } from 'vuex'
import config from 'config'
import BannersBlockCms from 'theme/components/core/blocks/Home/BannersBlockCms'
import MainBannersBlockCms from 'theme/components/core/blocks/Home/MainBannersBlockCms'
import LogosBlockCms from 'theme/components/core/blocks/Home/LogosBlockCms'
import ProductTabs from 'theme/components/core/blocks/Home/ProductTabs'
import CircleCategories from 'theme/components/core/blocks/Home/CircleCategories'
import LogoCategories from 'theme/components/core/blocks/Home/LogoCategories'
import ProductsCarousel from 'theme/components/core/ProductsCarousel.vue'
import Logo from 'theme/components/core/Logo'

export default {
  data () {
    return {
      loading: true
    }
  },
  components: {
    Logo,
    BannersBlockCms,
    LogoCategories,
    CircleCategories,
    ProductsCarousel,
    LazyHydrate,
    MainBannersBlockCms,
    LogosBlockCms,
    ProductTabs
  },
  computed: {
    ...mapGetters('user', ['isLoggedIn']),
    ...mapGetters('homepage', ['getPromoBlockCollection', 'getBestsellers', 'getNews', 'getSales']),
    isLazyHydrateEnabled () {
      return config.ssr.lazyHydrateFor.some(
        field => ['homepage', 'homepage.promo_block', 'homepage.bestsellers'].includes(field)
      )
    },
    isAuthElem () {
      return this.$store.state.ui.isAuthElem
    },
    validBaners () {
      return this.$store.state.cmsBlock.baners
    },
    productsByTypes () {
      // here is the product tabs definition
      return {
        'Bestseller': this.getBestsellers,
        'New': this.getNews,
        'Sale': this.getSales
      }
    }
  },
  mounted () {
    if (!this.isLoggedIn && localStorage.getItem('redirect')) {
      this.$bus.$emit('modal-show', 'modal-signup')
    }
  },
  watch: {
    isLoggedIn () {
      const redirectObj = localStorage.getItem('redirect')
      if (redirectObj) this.$router.push(redirectObj)
      localStorage.removeItem('redirect')
    },
    isAuthElem () {
      if (!this.isAuthElem) localStorage.removeItem('redirect')
    }
  },
  async asyncData ({ store, route, context }) { // this is for SSR purposes to prefetch data
    if (context) context.output.cacheTags.add(`home`)
    Logger.info('Calling asyncData in Home Page (core)')()

    await Promise.all([
      store.dispatch('cmsBlock/list', { filterValues: null }),
      store.dispatch('homepage/loadSales'),
      store.dispatch('homepage/loadNews'),
      store.dispatch('homepage/loadPromoBlock'),
      store.dispatch('homepage/loadBestsellers')
    ])
    for (let type of Object.keys(store.state.cmsBlock.baners)) {
      for (let baner of store.state.cmsBlock.baners[type]) {
        if (context) context.output.cacheTags.add('CMSB' + baner.id)
      }
    }
  },
  async beforeRouteEnter (to, from, next) {
    if (isServer) next() // SSR no need to invoke SW caching here
    else if (!from.name) { // Loading products to cache on SSR render
      next(async vm =>
        Promise.all([
          vm.$store.commit('ui/routeChanged', { to: to, from: from }),
          vm.$store.dispatch('cmsBlock/list', { filterValues: null }),
          vm.$store.dispatch('homepage/loadSales'),
          vm.$store.dispatch('homepage/loadNews'),
          vm.$store.dispatch('homepage/loadPromoBlock'),
          vm.$store.dispatch('homepage/loadBestsellers')
        ]).then(res => {
          vm.loading = false
        })
      )
    } else {
      next(async vm => {
        vm.$store.commit('ui/routeChanged', { to: to, from: from })
      })
    }
  },
  metaInfo () {
    return {
      title: 'Oficjalny sklep wydawnictwa Burda i Słowne',
      meta: [
        {
          vmid: 'description',
          name: 'description',
          content: 'Prenumerata prasy i wydania pojedyncze magazynów. Książki, e-booki i audiobooki. E-wykroje Burda. Kolekcje kart i figurek. Sprawdź w kultowy.pl'
        }
      ]
    }
  }
}
</script>

<style lang="scss" scoped>
  .new-collection {
    @media (max-width: 767px) {
      padding-top: 0;
      margin-bottom: 0;
    }
    h2 {
      @media (max-width: 767px) {
        padding: 10px 0;
        font-size: 16px;
      }
    }
  }
  .logo-box {
    border-bottom-left-radius: 15px;
    border-bottom-right-radius: 15px;
  }
</style>
